/**
 * DeliveryTicketDashboard.tsx
 *
 * A single-file React component that fetches:
 *  - DeliveryTicket data (/api/user/edgelink/delivery_tickets/) [lite version now, fewer fields]
 *  - Company data for current user (/api/user/edgelink/companies/0/)
 *
 * Displays tickets in a table with expand/collapse, includes filter & search,
 * pagination, a right-hand side drawer for ticket details (fetched on-demand),
 * and a create/update delivery ticket form in a dialog (fetched on-demand if editing) with signature capture.
 *
 * Additionally, includes a "Scan QR" icon next to "Add Item". On click, attempts
 * to use phone camera for scanning. If unavailable, shows a fallback input
 * so user can manually enter the device_serial or the entire QR URL.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  InputLabel,
  TablePagination,
  Divider,
  List,
  ListItem,
  ListItemText,
  Drawer,
  useMediaQuery,
  useTheme,
  Checkbox,
  DialogContentText,
  CircularProgressProps,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import axios from '../../../utils/axios.utils';

// ------------------------------------
// Data Models
// ------------------------------------
interface DeliveryTicketItem {
  edgelink_delivery_ticket_item_id?: number; // optional for new items
  edgelink_product_profile_id?: number | null;
  product_name: string;
  crop: string;
  lot_number: string;
  batch_number: string;
  seed_treatment_package: string;
  additional_notes: string;
  seed_unit_size: string;
  seed_size: string;
  seed_units: string;
}

interface DeliveryTicketCompanyDetail {
  edgelink_company_id: number | null;
  name: string | null;
  main_contact: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

interface DeliveryTicketCompanies {
  primary: DeliveryTicketCompanyDetail;
  secondary: DeliveryTicketCompanyDetail;
  tertiary: DeliveryTicketCompanyDetail;
}

interface DeliveryTicket {
  edgelink_delivery_ticket_id: number;
  companies: DeliveryTicketCompanies; // partial from the lite fetch
  delivery_ticket_number: string;
  delivered_at?: string | null; // might be omitted initially (lite), but present after detail fetch
  notes: string;
  received_by_name?: string; // might be omitted initially (lite), but present after detail fetch
  received_by_signature?: string; // might be omitted initially (lite), but present after detail fetch
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  status: boolean;
  items?: DeliveryTicketItem[]; // only set after detail fetch
}

interface DeliveryTicketDetail extends DeliveryTicket {
  // For clarity if needed, same structure but guaranteed to have all fields:
  items: DeliveryTicketItem[];
  delivered_at: string | null;
  received_by_name: string;
  received_by_signature: string;
}

// Company Data from /api/user/edgelink/companies/0
interface AssociatedCompany {
  edgelink_company_id: number;
  name: string;
  main_contact: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

interface MyCompanyData {
  edgelink_company_id: number;
  name: string;
  main_contact: string;
  main_contact_phone: string;
  main_contact_email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  parent_companies: AssociatedCompany[];
  associated_companies: AssociatedCompany[];
}

// A subset of form fields for creating/updating a ticket.
interface TicketFormState {
  edgelink_delivery_ticket_id?: number;
  delivery_ticket_number: string;
  delivered_at: string | null;
  notes: string;
  received_by_name: string;
  received_by_signature: string;
  printed_name?: string; // optional example field
  status: boolean;
  items: DeliveryTicketItem[];

  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;

  // Additional form states for UI only
  autoIncrementTicketNumber?: boolean;
  selectedAssociatedCompanyId?: number | null; // End customer
  selectedFormCompanyId?: number | null; // "Ticket Form" company
}

// ------------------------------------
// Helper to format city/state/zip
// ------------------------------------
function formatCityStateZip(c: DeliveryTicketCompanyDetail | AssociatedCompany | MyCompanyData) {
  const parts = [c.city, c.state, c.zip].filter(Boolean);
  return parts.join(', ');
}

// ------------------------------------
// Main Component
// ------------------------------------
const DeliveryTicketDashboard: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Data + UI states
  const [tickets, setTickets] = useState<DeliveryTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<DeliveryTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Expandable row logic
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // We'll store detailed ticket data in a map if needed for expand
  const [ticketDetailsMap, setTicketDetailsMap] = useState<{
    [key: number]: DeliveryTicketDetail;
  }>({});

  // For row expansion loading
  const [rowLoading, setRowLoading] = useState<number | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Filter & Search dialogs
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  // Basic filter examples
  type StatusFilterType = 'all' | 'active' | 'inactive';
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('all');

  type DateFilterType = 'none' | 'last7days' | 'last30days';
  const [dateFilter, setDateFilter] = useState<DateFilterType>('none');

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Ticket detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<DeliveryTicketDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Create/Update Form
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formData, setFormData] = useState<TicketFormState | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const isEditMode = !!formData?.edgelink_delivery_ticket_id;

  // My Company Data from /api/user/edgelink/companies/0
  const [myCompany, setMyCompany] = useState<MyCompanyData | null>(null);

  const today = new Date().toISOString().split('T')[0];

  // ------------------------------------
  // Fetch data from API (lite version)
  // ------------------------------------
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get<{
        success: boolean;
        data: DeliveryTicket[];
      }>('/api/user/edgelink/delivery_tickets/?lite=true', {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      });

      if (response.data && response.data.success) {
        // Store only the lite info
        const liteTickets = response.data.data.map((t) => ({
          edgelink_delivery_ticket_id: t.edgelink_delivery_ticket_id,
          companies: t.companies, // keep partial companies for listing/search
          delivery_ticket_number: t.delivery_ticket_number,
          notes: t.notes,
          created_at: t.created_at,
          created_by: t.created_by,
          updated_at: t.updated_at,
          updated_by: t.updated_by,
          status: t.status,
        })) as DeliveryTicket[];

        setTickets(liteTickets);
        setFilteredTickets(liteTickets);
        setError('');
      } else {
        setTickets([]);
        setFilteredTickets([]);
        setError('Failed to fetch delivery ticket data');
      }
    } catch (err) {
      console.error(err);
      setError('Error fetching delivery ticket data');
      setTickets([]);
      setFilteredTickets([]);
    }
    setLoading(false);
  };

  // Fetch the user's main company details
  const fetchMyCompany = async () => {
    try {
      const resp = await axios.get<{
        success: boolean;
        message: string;
        results: MyCompanyData;
      }>('/api/user/edgelink/companies/0/', {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      });
      if (resp.data && resp.data.success) {
        setMyCompany(resp.data.results);
      }
    } catch (err) {
      console.error('Error fetching company data', err);
    }
  };

  // Fetch a single ticket's detail by ID
  const fetchTicketDetail = async (ticketId: number): Promise<DeliveryTicketDetail | null> => {
    try {
      const response = await axios.get<{
        success: boolean;
        data: DeliveryTicketDetail;
      }>(`/api/user/edgelink/delivery_tickets/${ticketId}/`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      });
      if (response.data && response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      console.error('Error fetching ticket detail', err);
    }
    return null;
  };

  useEffect(() => {
    fetchTickets();
    fetchMyCompany();
    // eslint-disable-next-line
  }, []);

  // ------------------------------------
  // Merge tertiary companies into myCompany.associated_companies (if missing)
  // ------------------------------------
  useEffect(() => {
    if (!myCompany) return;
    if (!tickets.length) return;

    const updatedAssociated = [...myCompany.associated_companies];
    const existingIds = new Set(updatedAssociated.map((co) => co.edgelink_company_id));
    let changed = false;

    tickets.forEach((ticket) => {
      const t = ticket.companies.tertiary;
      if (t.edgelink_company_id && !existingIds.has(t.edgelink_company_id)) {
        updatedAssociated.push({
          edgelink_company_id: t.edgelink_company_id,
          name: t.name ?? '',
          main_contact: t.main_contact,
          main_contact_phone: t.main_contact_phone,
          main_contact_email: t.main_contact_email,
          address: t.address,
          city: t.city,
          state: t.state,
          zip: t.zip,
          country: t.country,
        });
        existingIds.add(t.edgelink_company_id);
        changed = true;
      }
    });

    if (changed) {
      setMyCompany((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          associated_companies: updatedAssociated,
        };
      });
    }
  }, [tickets, myCompany]);

  // ------------------------------------
  // Filter & search logic
  // ------------------------------------
  useEffect(() => {
    let results = [...tickets];

    // Search
    const term = searchTerm.toLowerCase();
    if (term) {
      results = results.filter((t) => {
        const ticketMatch = t.delivery_ticket_number.toLowerCase().includes(term);
        const notesMatch = t.notes.toLowerCase().includes(term);

        const primaryName = t.companies?.primary?.name || '';
        const secondaryName = t.companies?.secondary?.name || '';
        const tertiaryName = t.companies?.tertiary?.name || '';
        const companyStr = [primaryName, secondaryName, tertiaryName].join(' ');
        const companyMatch = companyStr.toLowerCase().includes(term);

        return ticketMatch || notesMatch || companyMatch;
      });
    }

    // Status filter
    if (statusFilter === 'active') {
      results = results.filter((t) => t.status === true);
    } else if (statusFilter === 'inactive') {
      results = results.filter((t) => t.status === false);
    }

    // Date filter
    if (dateFilter !== 'none') {
      const now = Date.now();
      if (dateFilter === 'last7days') {
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        results = results.filter((t) => {
          const created = new Date(t.created_at).getTime();
          return now - created <= sevenDays;
        });
      } else if (dateFilter === 'last30days') {
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        results = results.filter((t) => {
          const created = new Date(t.created_at).getTime();
          return now - created <= thirtyDays;
        });
      }
    }

    setFilteredTickets(results);
    setPage(0);
  }, [tickets, searchTerm, statusFilter, dateFilter]);

  // ------------------------------------
  // Expand / collapse row (fetch detail if needed)
  // ------------------------------------
  const toggleExpand = async (ticketId: number) => {
    setExpandedRows((prev) => {
      const copy = new Set(prev);
      if (copy.has(ticketId)) {
        copy.delete(ticketId);
      } else {
        copy.add(ticketId);
      }
      return copy;
    });

    // If we don't have details yet, fetch them
    if (!ticketDetailsMap[ticketId]) {
      setRowLoading(ticketId);
      const detail = await fetchTicketDetail(ticketId);
      setRowLoading(null);
      if (detail) {
        setTicketDetailsMap((prev) => ({
          ...prev,
          [ticketId]: detail,
        }));
      }
    }
  };

  // ------------------------------------
  // Drawer logic: fetch detail on row click
  // ------------------------------------
  const handleRowClick = async (ticket: DeliveryTicket) => {
    setDrawerOpen(true);
    setSelectedTicket(null);
    setDrawerLoading(true);

    const detail = await fetchTicketDetail(ticket.edgelink_delivery_ticket_id);
    setDrawerLoading(false);
    if (detail) {
      setSelectedTicket(detail);
    }
  };

  const closeDrawer = () => {
    setSelectedTicket(null);
    setDrawerOpen(false);
  };

  // Pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ------------------------------------
  // Create/Update Form logic
  // Fetch detail if editing
  // ------------------------------------
  const handleOpenForm = async (ticket?: DeliveryTicket) => {
    // Open the dialog immediately with loading state
    setFormDialogOpen(true);
    setFormData(null); // Reset form data to show loading state

    // Creating new
    if (!ticket) {
      // Start with a fresh form after a short delay to show the loading state
      setTimeout(() => {
        setFormData({
          delivery_ticket_number: '',
          delivered_at: today,
          notes: '',
          received_by_name: '',
          received_by_signature: '',
          printed_name: '',
          status: true,
          items: [],
          autoIncrementTicketNumber: true,
          selectedAssociatedCompanyId: null,
          selectedFormCompanyId: null,
        });
      }, 300); // Brief delay to show loading state
      return;
    }

    // Editing existing
    setFormLoading(true);
    const detail = await fetchTicketDetail(ticket.edgelink_delivery_ticket_id);
    setFormLoading(false);
    if (!detail) {
      alert('Could not load ticket details for editing.');
      setFormDialogOpen(false);
      return;
    }

    const formCompanyId = detail.companies?.primary?.edgelink_company_id || null;
    const endCustomerId = detail.companies?.tertiary?.edgelink_company_id || null;

    setFormData({
      edgelink_delivery_ticket_id: detail.edgelink_delivery_ticket_id,
      delivery_ticket_number: detail.delivery_ticket_number,
      delivered_at: detail.delivered_at,
      notes: detail.notes,
      received_by_name: detail.received_by_name,
      received_by_signature: detail.received_by_signature,
      printed_name: detail.received_by_name,
      status: detail.status,
      items: detail.items || [],

      created_at: detail.created_at,
      created_by: detail.created_by,
      updated_at: detail.updated_at,
      updated_by: detail.updated_by,

      autoIncrementTicketNumber: false,
      selectedAssociatedCompanyId: endCustomerId,
      selectedFormCompanyId: formCompanyId,
    });
  };

  const handleCloseForm = () => {
    setFormData(null);
    setFormDialogOpen(false);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchTickets();
  };

  // ------------------------------------
  // Rendering table rows
  // ------------------------------------
  const renderTableRows = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageTickets = filteredTickets.slice(startIndex, endIndex);

    return pageTickets.map((ticket) => {
      const isExpanded = expandedRows.has(ticket.edgelink_delivery_ticket_id);
      const detail = ticketDetailsMap[ticket.edgelink_delivery_ticket_id];

      return (
        <React.Fragment key={ticket.edgelink_delivery_ticket_id}>
          <TableRow
            hover
            onClick={() => handleRowClick(ticket)}
            sx={{
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f9fbfc' },
            }}
          >
            <TableCell sx={{ width: 40, height: '40px' }}>
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  await toggleExpand(ticket.edgelink_delivery_ticket_id);
                }}
              >
                {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </TableCell>
            <TableCell sx={{ height: '40px' }}>{ticket.delivery_ticket_number}</TableCell>
            <TableCell sx={{ height: '40px' }}>{getCompanyNames(ticket.companies)}</TableCell>
            <TableCell sx={{ height: '40px' }}>{ticket.notes}</TableCell>
            <TableCell sx={{ height: '40px' }}>{ticket.received_by_name || ''}</TableCell>
            <TableCell sx={{ height: '40px' }}>{new Date(ticket.created_at).toLocaleString()}</TableCell>
            <TableCell sx={{ height: '40px' }}>{ticket.status ? 'Active' : 'Inactive'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              colSpan={7}
              sx={{
                p: 0,
                borderBottom: isExpanded ? '1px solid rgba(224, 224, 224, 1)' : 'none',
              }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, backgroundColor: '#f4f6f8' }}>
                  {rowLoading === ticket.edgelink_delivery_ticket_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {!rowLoading && detail && (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Ticket Items
                      </Typography>
                      {detail.items && detail.items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No items found for this ticket.
                        </Typography>
                      ) : (
                        <Table size="small" sx={{ mb: 2 }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#eef1f4' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Crop</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Lot #</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Batch #</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Seed Treatment</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Units</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.items?.map((item, idx) => (
                              <TableRow key={item.edgelink_delivery_ticket_item_id || idx}>
                                <TableCell>{item.product_name}</TableCell>
                                <TableCell>{item.crop}</TableCell>
                                <TableCell>{item.lot_number || 'N/A'}</TableCell>
                                <TableCell>{item.batch_number || 'N/A'}</TableCell>
                                <TableCell>{item.seed_treatment_package || 'N/A'}</TableCell>
                                <TableCell>{item.seed_units}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    });
  };

  // ------------------------------------
  // Render
  // ------------------------------------
  return (
    <Box sx={{ p: 2, backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Title + (Center) Search + Filter/Add Buttons */}
      <Paper
        elevation={4}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          backgroundColor: '#ffffff',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          Delivery Ticket Dashboard
        </Typography>

        {/* Centered inline search box, hidden on mobile */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
        </Box>

        {/* Right side: Filter, old search, Add */}
        <Stack direction="row" spacing={2}>
          <IconButton onClick={() => setFilterDialogOpen(true)}>
            <FilterListIcon />
          </IconButton>
          <IconButton onClick={() => setSearchDialogOpen(true)}>
            <SearchIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()}>
            Add New Ticket
          </Button>
        </Stack>
      </Paper>

      {/* Loading & Error */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Table of tickets */}
      {!loading && !error && (
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table
              size="small"
              sx={{
                minWidth: 800,
                // Uniform cell padding removed to fix extra spacing
                '& td, & th': { padding: '0px !important' },
              }}
              aria-label="tickets table"
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eef1f4' }}>
                  <TableCell sx={{ width: 40, fontWeight: 'bold', height: '40px' }} />
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Ticket #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Companies</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Received By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', height: '40px' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderTableRows()}</TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredTickets.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Drawer for ticket details (shows loading if needed) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: isSmallScreen ? '100%' : '700px',
            minWidth: '400px',
            maxWidth: '90vw',
            p: 2,
            resize: 'horizontal',
            overflow: 'auto',
            backgroundColor: '#fefefe',
          },
        }}
      >
        {drawerLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!drawerLoading && selectedTicket && (
          <TicketDetailsDrawerContent
            ticket={selectedTicket}
            onClose={closeDrawer}
            onEdit={() => {
              handleOpenForm(selectedTicket);
              closeDrawer();
            }}
          />
        )}
      </Drawer>

      {/* Filter Dialog */}
      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
      />

      {/* Search Dialog */}
      <SearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filteredTickets={filteredTickets}
      />

      {/* Create/Update Ticket Form Dialog */}
      <TicketFormDialog
        open={formDialogOpen}
        onClose={handleCloseForm}
        formData={formData}
        setFormData={setFormData}
        onSuccess={handleFormSuccess}
        isEdit={isEditMode}
        myCompany={myCompany}
        loading={formLoading}
      />
    </Box>
  );
};

// ------------------------------------
// Detail Drawer Content
// ------------------------------------
function TicketDetailsDrawerContent({
  ticket,
  onClose,
  onEdit,
}: {
  ticket: DeliveryTicketDetail;
  onClose: () => void;
  onEdit: () => void;
}) {
  const deliveryDate = ticket.delivered_at ? new Date(ticket.delivered_at).toLocaleDateString() : 'N/A';

  const { primary, secondary, tertiary } = ticket.companies;

  const cityStateZipPrimary = formatCityStateZip(primary);
  const cityStateZipSecondary = formatCityStateZip(secondary);
  const cityStateZipTertiary = formatCityStateZip(tertiary);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Delivery Ticket Details
        </Typography>
        <Stack direction="row">
          <IconButton onClick={onEdit} aria-label="Edit Ticket" size="small">
            <EditIcon />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Divider sx={{ my: 2 }} />

      {/* Primary (Ticket Form) & Ticket # */}
      <Box sx={{ textAlign: 'left' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {primary.name || 'Ticket Form'}
            </Typography>
            <Typography variant="body2">
              <strong>Address:</strong> {primary.address || ''}
            </Typography>
            <Typography variant="body2">
              <strong>City/State/Zip:</strong> {cityStateZipPrimary}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {primary.main_contact_email || ''}
            </Typography>
            <Typography variant="body2">
              <strong>Phone:</strong> {primary.main_contact_phone || ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Ticket #: bigger variant */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ticket #: {ticket.delivery_ticket_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Delivery Date: {deliveryDate}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Dealer (secondary) & Customer (tertiary) */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Dealer */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Dealer
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {secondary.name || ''}
            </Typography>
            <Typography variant="body2">
              <strong>Address:</strong> {secondary.address || ''}
            </Typography>
            <Typography variant="body2">
              <strong>City, State, Zip:</strong> {cityStateZipSecondary}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {secondary.main_contact_email || ''}
            </Typography>
            <Typography variant="body2">
              <strong>Phone:</strong> {secondary.main_contact_phone || ''}
            </Typography>
          </Grid>

          {/* End Customer */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              End Customer
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {tertiary.name || ''}
            </Typography>
            <Typography variant="body2">
              <strong>Address:</strong> {tertiary.address || ''}
            </Typography>
            <Typography variant="body2">
              <strong>City, State, Zip:</strong> {cityStateZipTertiary}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {tertiary.main_contact_email || ''}
            </Typography>
            <Typography variant="body2">
              <strong>Phone:</strong> {tertiary.main_contact_phone || ''}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Items */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Ticket Items
      </Typography>
      {ticket.items && ticket.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No items found for this ticket.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#eef1f4' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Crop</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Lot #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Batch #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Seed Treat</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Units</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticket.items.map((item, idx) => (
              <TableRow key={item.edgelink_delivery_ticket_item_id || idx}>
                <TableCell>{item.product_name}</TableCell>
                <TableCell>{item.crop}</TableCell>
                <TableCell>{item.lot_number || 'N/A'}</TableCell>
                <TableCell>{item.batch_number || 'N/A'}</TableCell>
                <TableCell>{item.seed_treatment_package || 'N/A'}</TableCell>
                <TableCell>{item.seed_units}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Notes */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Notes
        </Typography>
        <Typography variant="body2">{ticket.notes || 'No notes'}</Typography>
      </Box>

      {/* Received By: side-by-side Signature + Printed Name */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Received By
        </Typography>
        <Grid container spacing={2}>
          {/* Signature */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Signature
            </Typography>
            {ticket.received_by_signature ? (
              <Box sx={{ mb: 1 }}>
                <img
                  src={ticket.received_by_signature}
                  alt="Signature"
                  style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                N/A
              </Typography>
            )}
          </Grid>

          {/* Printed Name */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Printed Name
            </Typography>
            <Typography variant="body2">{ticket.received_by_name || 'N/A'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Footer: created/updated */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Created: {new Date(ticket.created_at).toLocaleString()} by {ticket.created_by}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Modified: {new Date(ticket.updated_at).toLocaleString()} by {ticket.updated_by}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

// ------------------------------------
// Create/Update Ticket Form
// ------------------------------------
function TicketFormDialog({
  open,
  onClose,
  formData,
  setFormData,
  onSuccess,
  isEdit,
  myCompany,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  formData: TicketFormState | null;
  setFormData: React.Dispatch<React.SetStateAction<TicketFormState | null>>;
  onSuccess: () => void;
  isEdit: boolean;
  myCompany: MyCompanyData | null;
  loading: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // For signature
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState<{ x: number; y: number } | null>(null);

  // For scanning
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [manualSerial, setManualSerial] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanSuccess, setScanSuccess] = useState<string | null>(null);

  // Track initial form state for unsaved-changes prompt
  const initialFormDataRef = useRef<string>(formData ? JSON.stringify(formData) : '');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Update initialFormDataRef when the form dialog is opened and formData becomes available
  const formDataInitializedRef = useRef(false);

  // Reset tracking when dialog opens/closes
  useEffect(() => {
    if (!open) {
      formDataInitializedRef.current = false;
    }
  }, [open]);

  // Set initial form data when it becomes available
  useEffect(() => {
    if (formData && !formDataInitializedRef.current) {
      initialFormDataRef.current = JSON.stringify(formData);
      formDataInitializedRef.current = true;
    }
  }, [formData]);

  // Build array for "Ticket Form" dropdown
  const possibleFormCompanies = useMemo(() => {
    if (!myCompany) return [];
    const arr = [...(myCompany.parent_companies || [])];
    arr.push({
      edgelink_company_id: myCompany.edgelink_company_id,
      name: myCompany.name,
      main_contact: myCompany.main_contact,
      main_contact_phone: myCompany.main_contact_phone,
      main_contact_email: myCompany.main_contact_email,
      address: myCompany.address,
      city: myCompany.city,
      state: myCompany.state,
      zip: myCompany.zip,
      country: myCompany.country,
    });
    return arr;
  }, [myCompany]);

  const selectedFormCompany = useMemo(() => {
    if (!formData || !possibleFormCompanies.length || !formData.selectedFormCompanyId) return null;
    return possibleFormCompanies.find((co) => co.edgelink_company_id === formData.selectedFormCompanyId);
  }, [possibleFormCompanies, formData?.selectedFormCompanyId]);

  const selectedAssociatedCompany = useMemo(() => {
    if (!formData || !myCompany?.associated_companies?.length || !formData.selectedAssociatedCompanyId) {
      return null;
    }
    return myCompany.associated_companies.find((co) => co.edgelink_company_id === formData.selectedAssociatedCompanyId);
  }, [myCompany, formData?.selectedAssociatedCompanyId]);

  // Pre-load signature on edit
  useEffect(() => {
    if (!canvasRef.current || !formData) return;
    if (!formData.received_by_signature) return;
    if (!isEdit) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    const img = new Image();
    img.src = formData.received_by_signature;
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
    };
  }, [isEdit, formData?.received_by_signature]);

  // Watch formData changes to detect unsaved changes
  useEffect(() => {
    if (!formData) {
      setUnsavedChanges(false);
      return;
    }

    const current = JSON.stringify(formData);
    const initial = initialFormDataRef.current;

    // Only check for changes if we have both current and initial values
    if (current && initial) {
      const hasChanges = current !== initial;
      console.log('Checking for unsaved changes:', { hasChanges });
      setUnsavedChanges(hasChanges);
    } else {
      console.log('Missing current or initial data for change detection');
      setUnsavedChanges(false);
    }
  }, [formData]);

  // Intercept dialog close if unsaved changes
  const handleAttemptClose = () => {
    console.log('Attempting to close dialog. Unsaved changes:', unsavedChanges);
    if (unsavedChanges) {
      console.log('Opening discard dialog');
      setDiscardDialogOpen(true);
      return;
    }
    console.log('No unsaved changes, closing directly');
    onClose();
  };

  // Handle confirmation of discarding changes
  const confirmDiscardChanges = () => {
    setDiscardDialogOpen(false);
    onClose();
  };

  // *** Handlers *** //
  const handleFormCompanyChange = (value: number) => {
    setFormData((prev) => {
      if (!prev) return null;
      return { ...prev, selectedFormCompanyId: value };
    });
  };

  const handleAutoIncrementChange = (checked: boolean) => {
    if (isEdit) return; // disabled in edit
    setFormData((prev) => {
      if (!prev) return null;
      let newTicketNumber = prev.delivery_ticket_number;
      if (checked && !newTicketNumber) {
        newTicketNumber = `TKT-${Date.now()}`;
      }
      return {
        ...prev,
        autoIncrementTicketNumber: checked,
        delivery_ticket_number: newTicketNumber,
      };
    });
  };

  const handleTicketNumberChange = (val: string) => {
    if (isEdit) return;
    setFormData((prev) => {
      if (!prev) return null;
      return { ...prev, delivery_ticket_number: val };
    });
  };

  const handleDeliveryDateChange = (val: string) => {
    setFormData((prev) => {
      if (!prev) return null;
      return { ...prev, delivered_at: val };
    });
  };

  const handleAssociatedCompanyChange = (companyId: number | null) => {
    setFormData((prev) => {
      if (!prev) return null;
      return { ...prev, selectedAssociatedCompanyId: companyId };
    });
  };

  // Signature handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    setLastPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current || !lastPos) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    const rect = canvasRef.current.getBoundingClientRect();
    const currentPos = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(currentPos.x, currentPos.y);
    ctx.stroke();

    setLastPos(currentPos);
  };
  const handlePointerUp = () => {
    setIsDrawing(false);
    setLastPos(null);
    if (canvasRef.current) {
      const dataURL = canvasRef.current.toDataURL('image/png');
      setFormData((prev) => {
        if (!prev) return null;
        return { ...prev, received_by_signature: dataURL };
      });
    }
  };
  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setFormData((prev) => {
          if (!prev) return null;
          return { ...prev, received_by_signature: '' };
        });
      }
    }
  };

  // Items handlers
  const handleItemChange = (index: number, field: keyof DeliveryTicketItem, value: any) => {
    setFormData((prev) => {
      if (!prev) return null;
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };
  const handleAddItem = () => {
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            product_name: '',
            crop: '',
            lot_number: '',
            batch_number: '',
            seed_treatment_package: '',
            additional_notes: '',
            seed_unit_size: '',
            seed_size: '',
            seed_units: '',
          },
        ],
      };
    });
  };
  const handleRemoveItem = (index: number) => {
    setFormData((prev) => {
      if (!prev) return null;
      const newItems = [...prev.items];
      newItems.splice(index, 1);
      return { ...prev, items: newItems };
    });
  };

  // *** QR scanning logic *** //
  const handleScanClick = () => {
    setScanDialogOpen(true);
    setScanError(null);
    setScanSuccess(null);
  };

  const handleManualScan = async () => {
    const serial = extractSerial(manualSerial.trim());
    if (!serial) {
      setScanError('Could not parse device_serial from input.');
      return;
    }
    await fetchProductProfile(serial);
  };

  const handleCameraScan = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      const fakeScannedUrl = 'https://agconnex.com/scan?device_serial=8ZHQJ5';
      const serial = extractSerial(fakeScannedUrl);
      if (!serial) {
        setScanError('Could not parse device_serial from scanned code.');
        return;
      }
      await fetchProductProfile(serial);
    } catch (err) {
      console.error(err);
      setScanError('Could not open camera or decode QR. Please enter manually instead.');
    }
  };

  const extractSerial = (input: string): string | null => {
    if (/^[A-Za-z0-9]+$/.test(input)) {
      return input;
    }
    const match = input.match(/[?&]device_serial=([^&#]+)/);
    if (match) {
      return match[1];
    }
    return null;
  };

  const fetchProductProfile = async (deviceSerial: string) => {
    try {
      const resp = await axios.get<{
        success: boolean;
        data: {
          device_serial: string;
          product_profile_id: number;
          product_name: string;
          lot_number: string;
          batch_number: string;
          crop: string;
          seed_units: string | null;
          seed_treatment_package: string | null;
          additional_notes: string | null;
          seed_size: string | null;
          seed_unit_size: string | null;
        };
      }>(`/api/user/edgelink/devices/${deviceSerial}/product_profile/`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      });
      if (resp.data.success) {
        const pd = resp.data.data;
        setFormData((prev) => {
          if (!prev) return null;
          const newItems = [...prev.items];
          newItems.push({
            edgelink_product_profile_id: pd.product_profile_id,
            product_name: pd.product_name,
            lot_number: pd.lot_number || '',
            batch_number: pd.batch_number || '',
            crop: pd.crop || '',
            seed_treatment_package: pd.seed_treatment_package || '',
            seed_units: pd.seed_units || '',
            additional_notes: pd.additional_notes || '',
            seed_size: pd.seed_size || '',
            seed_unit_size: pd.seed_unit_size || '',
          });
          return { ...prev, items: newItems };
        });
        setScanSuccess(`Added item for product: ${pd.product_name}`);
        setScanError(null);
        setManualSerial('');
      } else {
        setScanError('Unable to fetch product profile. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setScanError('Server error. Could not save product profile.');
    }
  };

  const handleSubmit = async () => {
    if (!formData) return;
    try {
      let dateToSave: string | null = null;
      if (formData.delivered_at) {
        const parsed = Date.parse(formData.delivered_at);
        if (!isNaN(parsed)) {
          dateToSave = new Date(parsed).toISOString();
        }
      }

      const payload = {
        ...formData,
        delivered_at: dateToSave,
        primary_edgelink_company_id: formData.selectedFormCompanyId, // Ticket Form
        secondary_edgelink_company_id: myCompany?.edgelink_company_id, // Dealer
        tertiary_edgelink_company_id: formData.selectedAssociatedCompanyId, // End Customer
      };

      if (isEdit && payload.edgelink_delivery_ticket_id) {
        await axios.put(`/api/user/edgelink/delivery_tickets/${payload.edgelink_delivery_ticket_id}/`, payload, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        });
      } else {
        await axios.post('/api/user/edgelink/delivery_tickets/', payload, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        });
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Failed to save. Check console for details.');
    }
  };

  // Don't render anything if the dialog is not open
  if (!open) {
    return null;
  }

  // If we're loading or formData is null, show a loading indicator
  if (loading || formData === null) {
    return (
      <Dialog
        open={open}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '80vw',
            height: '80vh',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>{isEdit ? 'Loading Delivery Ticket...' : 'Creating New Delivery Ticket...'}</DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (!formData) return null;

  // A local function to format city/state/zip for MyCompany or AssociatedCompany
  const formatCityStateZipLocal = (co: AssociatedCompany | MyCompanyData) =>
    [co.city, co.state, co.zip].filter(Boolean).join(', ');

  return (
    <>
      <Dialog
        open={open}
        onClose={handleAttemptClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '80vw',
            height: '80vh',
            borderRadius: 2,
            resize: 'both',
            overflow: 'auto',
          },
        }}
      >
        {/* Discard dialog */}
        <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
          <DialogTitle>Discard Unsaved Changes?</DialogTitle>
          <DialogContent>
            <DialogContentText>You have unsaved changes. Closing now will discard them.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDiscardDialogOpen(false)}>Keep Editing</Button>
            <Button color="error" variant="contained" onClick={confirmDiscardChanges}>
              Discard
            </Button>
          </DialogActions>
        </Dialog>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {isEdit ? 'Edit Delivery Ticket' : 'Create Delivery Ticket'}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.status}
                onChange={(e) =>
                  setFormData((prev) => {
                    if (!prev) return null;
                    return { ...prev, status: e.target.checked };
                  })
                }
              />
            }
            label="Active"
          />
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* "Ticket Form" with top margin to align with "Ticket Number" */}
            <Grid item xs={12} md={6} sx={{ mt: { xs: 2, md: 0 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Ticket Form
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select a company</InputLabel>
                <Select
                  value={formData.selectedFormCompanyId || ''}
                  label="Select a company"
                  onChange={(e) => handleFormCompanyChange(Number(e.target.value))}
                  disabled={isEdit}
                >
                  <MenuItem value="">--Select--</MenuItem>
                  {possibleFormCompanies.map((co) => (
                    <MenuItem key={co.edgelink_company_id} value={co.edgelink_company_id}>
                      {co.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedFormCompany && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedFormCompany.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>City, State:</strong> {formatCityStateZipLocal(selectedFormCompany)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedFormCompany.main_contact_email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedFormCompany.main_contact_phone}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* RIGHT: Auto increment, Ticket Number, Delivery Date */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.autoIncrementTicketNumber}
                    onChange={(e) => handleAutoIncrementChange(e.target.checked)}
                    disabled={isEdit}
                  />
                }
                label="Auto Increment Ticket #"
                sx={{ mb: 1 }}
              />
              <TextField
                label="Ticket Number"
                fullWidth
                placeholder="Ex: TKT-12345"
                helperText={
                  isEdit
                    ? 'Ticket number cannot be changed in Edit mode.'
                    : 'If auto-increment is checked, this is auto-generated.'
                }
                value={formData.delivery_ticket_number}
                onChange={(e) => handleTicketNumberChange(e.target.value)}
                disabled={isEdit || !!formData.autoIncrementTicketNumber}
              />
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Delivery Date"
                  type="date"
                  fullWidth
                  value={formData.delivered_at ? formData.delivered_at.split('T')[0] : ''}
                  onChange={(e) => handleDeliveryDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Dealer and End Customer */}
          <Grid container spacing={2}>
            {/* Dealer */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Dealer
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Dealer</InputLabel>
                <Select value={myCompany?.edgelink_company_id || ''} label="Dealer" disabled>
                  {myCompany && <MenuItem value={myCompany.edgelink_company_id}>{myCompany.name}</MenuItem>}
                </Select>
              </FormControl>
              {myCompany && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Address:</strong> {myCompany.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>City, State:</strong> {formatCityStateZipLocal(myCompany)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {myCompany.main_contact_email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {myCompany.main_contact_phone}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* End Customer */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                End Customer
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Choose Customer</InputLabel>
                <Select
                  value={formData.selectedAssociatedCompanyId || ''}
                  label="Choose Customer"
                  onChange={(e) => {
                    const { value } = e.target;
                    handleAssociatedCompanyChange(value === '' ? null : Number(value));
                  }}
                >
                  <MenuItem value="">--Select--</MenuItem>
                  {myCompany?.associated_companies.map((co) => (
                    <MenuItem key={co.edgelink_company_id} value={co.edgelink_company_id}>
                      {co.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedAssociatedCompany && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedAssociatedCompany.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>City, State:</strong> {formatCityStateZipLocal(selectedAssociatedCompany)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedAssociatedCompany.main_contact_email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedAssociatedCompany.main_contact_phone}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Delivery Ticket Items */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Delivery Ticket Items
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eef1f4' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Crop</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Lot #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Batch #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Seed Treatment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Unit Size</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Seed Size</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Units</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="e.g., Hybrid XYZ"
                        value={item.product_name}
                        onChange={(e) => handleItemChange(idx, 'product_name', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="e.g., Corn"
                        value={item.crop}
                        onChange={(e) => handleItemChange(idx, 'crop', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="12345"
                        value={item.lot_number}
                        onChange={(e) => handleItemChange(idx, 'lot_number', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="67890"
                        value={item.batch_number}
                        onChange={(e) => handleItemChange(idx, 'batch_number', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Fungicide / Insecticide"
                        value={item.seed_treatment_package}
                        onChange={(e) => handleItemChange(idx, 'seed_treatment_package', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="e.g., 80K"
                        value={item.seed_unit_size}
                        onChange={(e) => handleItemChange(idx, 'seed_unit_size', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="e.g., Medium Round"
                        value={item.seed_size}
                        onChange={(e) => handleItemChange(idx, 'seed_size', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="e.g., 100"
                        value={item.seed_units}
                        onChange={(e) => handleItemChange(idx, 'seed_units', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Additional notes..."
                        value={item.additional_notes}
                        onChange={(e) => handleItemChange(idx, 'additional_notes', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleRemoveItem(idx)}>
                        <RemoveIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Buttons: Add Item / Scan QR */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddItem}>
              Add Item
            </Button>
            <Button variant="outlined" startIcon={<QrCodeScannerIcon />} onClick={handleScanClick}>
              Scan QR
            </Button>
          </Stack>

          {/* Notes below items */}
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Overall Notes"
              fullWidth
              multiline
              minRows={2}
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => {
                  if (!prev) return null;
                  return { ...prev, notes: e.target.value };
                })
              }
              placeholder="Any extra notes about this delivery..."
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Received By: Printed Name above the signature, both same width */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Received By
            </Typography>

            {/* Printed Name - full width */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Printed Name
              </Typography>
              <TextField
                fullWidth
                value={formData.received_by_name}
                onChange={(e) =>
                  setFormData((prev) => {
                    if (!prev) return null;
                    return { ...prev, received_by_name: e.target.value };
                  })
                }
                placeholder="John Doe"
              />
            </Box>

            {/* Signature - raised height */}
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Signature
            </Typography>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                maxWidth: '600px',
                height: 120,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <canvas
                ref={canvasRef}
                width={600}
                height={120}
                style={{ touchAction: 'none', width: '100%', height: '100%' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" onClick={clearSignature}>
                Clear
              </Button>
              {formData.received_by_signature ? (
                <Typography variant="caption" sx={{ alignSelf: 'center', ml: 1 }}>
                  (Signature captured)
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ alignSelf: 'center', ml: 1 }}>
                  (No signature yet)
                </Typography>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions>
          {/* Hide created/updated on mobile */}
          {!isMobile && isEdit && formData.created_by && (
            <Box sx={{ flexGrow: 1, mr: 2 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                Created: {new Date(formData.created_at || '').toLocaleString()} by {formData.created_by}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Updated: {new Date(formData.updated_at || '').toLocaleString()} by {formData.updated_by}
              </Typography>
            </Box>
          )}
          <Button onClick={handleAttemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan QR Dialog */}
      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)}>
        <DialogTitle>Scan QR / Enter Device Serial</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can use your camera (on mobile) or type/paste the URL / device serial manually.
          </DialogContentText>
          {/* Inline messages for scanning */}
          {scanError && (
            <Alert severity="error" sx={{ my: 1 }}>
              {scanError}
            </Alert>
          )}
          {scanSuccess && (
            <Alert severity="success" sx={{ my: 1 }}>
              {scanSuccess}
            </Alert>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleCameraScan}>
              Use Camera
            </Button>
            <Box>
              <TextField
                label="Manual Entry (URL or device_serial)"
                size="small"
                value={manualSerial}
                onChange={(e) => setManualSerial(e.target.value)}
              />
              <Button variant="contained" sx={{ ml: 1 }} onClick={handleManualScan}>
                Submit
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ------------------------------------
// Filter Dialog
// ------------------------------------
interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  statusFilter: 'all' | 'active' | 'inactive';
  setStatusFilter: (val: 'all' | 'active' | 'inactive') => void;
  dateFilter: 'none' | 'last7days' | 'last30days';
  setDateFilter: (val: 'none' | 'last7days' | 'last30days') => void;
}

function FilterDialog({ open, onClose, statusFilter, setStatusFilter, dateFilter, setDateFilter }: FilterDialogProps) {
  const handleClear = () => {
    setStatusFilter('all');
    setDateFilter('none');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Filter Tickets</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Status
            </Typography>
            <RadioGroup
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              row
            >
              <FormControlLabel value="all" control={<Radio />} label="All" />
              <FormControlLabel value="active" control={<Radio />} label="Active" />
              <FormControlLabel value="inactive" control={<Radio />} label="Inactive" />
            </RadioGroup>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Created Date
            </Typography>
            <FormControl fullWidth>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as 'none' | 'last7days' | 'last30days')}
              >
                <MenuItem value="none">No Filter</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClear}>
          Clear
        </Button>
        <Button variant="contained" onClick={onClose}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------
// Search Dialog
// ------------------------------------
interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  filteredTickets: DeliveryTicket[];
}

function SearchDialog({ open, onClose, searchTerm, setSearchTerm, filteredTickets }: SearchDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Search Tickets
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          margin="dense"
          label="Search by ticket #, notes, company..."
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {filteredTickets.length > 0 ? (
          <List dense sx={{ mt: 2, maxHeight: 200, overflowY: 'auto', border: '1px solid #eee' }}>
            {filteredTickets.map((ticket) => (
              <ListItem
                key={ticket.edgelink_delivery_ticket_id}
                button
                onClick={() => {
                  onClose();
                }}
              >
                <ListItemText
                  primary={`Ticket: ${ticket.delivery_ticket_number}`}
                  secondary={getCompanyNames(ticket.companies)}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" sx={{ mt: 2 }}>
            No matching tickets found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------
// Helper
// ------------------------------------
function getCompanyNames(companies: DeliveryTicketCompanies): string {
  const names: string[] = [];
  if (companies.primary?.name) names.push(companies.primary.name);
  if (companies.secondary?.name) names.push(companies.secondary.name);
  if (companies.tertiary?.name) names.push(companies.tertiary.name);
  return names.join(', ');
}

export default DeliveryTicketDashboard;
